import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import {
  getProject,
  logVisit,
  verifyMilestone,
  signSubmitDbns,
  downloadInspectionPdf,
  downloadConstructionPacket,
  addProjectNote,
  sendProjectMessage,
  listProjectDocuments,
  downloadProjectDocument,
} from "../lib/architectPortalB";
import { uploadFile } from "../lib/applications";
import { UploadedRow } from "./RegisterArchitect";
import { Toaster, toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Camera,
  CheckCircle2,
  Circle,
  Download,
  FileText,
  Hammer,
  MessageSquare,
  NotebookPen,
  Send,
  ShieldCheck,
  Upload,
} from "lucide-react";
import BackBar from "../components/BackBar";
import StatusPill from "../components/StatusPill";
import WorkflowStrip from "../components/WorkflowStrip";
import { assetUrl } from "../lib/format";
import { projectNextAction } from "../lib/projectNextAction";

const ORDERED = ["foundation", "rough_in", "final_occupancy"];
const MILESTONE_LABEL = {
  foundation: "Foundation",
  rough_in: "Rough-in",
  final_occupancy: "Final / Occupancy",
};

export default function ArchitectWorkspace() {
  const { projectId } = useParams();
  const { user } = useRole();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [err, setErr] = useState(null);
  const [documents, setDocuments] = useState(null);

  // Drawers / dialogs
  const [logFor, setLogFor] = useState(null); // milestone_key for inspection visit
  const [verifyFor, setVerifyFor] = useState(null);
  const [showSign, setShowSign] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [packetBusy, setPacketBusy] = useState(false);

  async function reload() {
    try {
      const p = await getProject(projectId, user.id);
      setProject(p);
    } catch (e) {
      setErr(e);
    }
  }

  useEffect(() => {
    if (!user?.id || !projectId) return;
    let alive = true;
    listProjectDocuments(projectId, user.id)
      .then((d) => alive && setDocuments(d.items || []))
      .catch(() => alive && setDocuments([]));
    return () => {
      alive = false;
    };
  }, [projectId, user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, user?.id]);

  if (err) {
    return (
      <section data-testid="ws-error" style={{ paddingTop: 12 }}>
        <BackBar
          parent={{ to: "/architect/projects", label: "Projects" }}
          trail={[
            { label: "Projects", to: "/architect/projects" },
            { label: "Not found" },
          ]}
        />
        <div
          className="mt-6 wn-card"
          style={{ padding: 24, color: "#A23B2E" }}
        >
          Could not load this project. It may have been removed.
        </div>
      </section>
    );
  }
  if (!project) {
    return (
      <section data-testid="ws-loading" style={{ paddingTop: 12 }}>
        <div style={{ color: "#6C6B85", fontStyle: "italic" }}>Loading…</div>
      </section>
    );
  }

  const stage = project.current_stage;
  const isCi = stage === "Construction Inspection";
  const isComplete = stage === "Complete";
  const isSigned = !!project.final_record_sent_to_city;
  const allVerified = ORDERED.every(
    (k) => (project.milestones || {})[k]?.status === "Verified",
  );
  const inspectionCount = (project.inspections || []).length;
  const packetReady = inspectionCount > 0 || isSigned;

  async function handleLogVisit(payload) {
    try {
      await logVisit(projectId, logFor, user.id, payload);
      await reload();
      setLogFor(null);
      toast.success("Visit logged.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log visit.");
    }
  }
  async function handleVerify() {
    try {
      await verifyMilestone(projectId, verifyFor, user.id);
      await reload();
      setVerifyFor(null);
      toast.success("Milestone verified.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not verify.");
    }
  }
  async function handleSignSubmit() {
    try {
      await signSubmitDbns(projectId, user.id);
      await reload();
      setShowSign(false);
      toast.success("Signed and submitted to DBNS.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sign-submit.");
    }
  }
  async function handleAddNote({ body, photos }) {
    try {
      await addProjectNote(projectId, user.id, body, photos);
      await reload();
      setNoteOpen(false);
      toast.success("Note saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save note.");
    }
  }
  async function handlePacketDownload() {
    if (!user?.id) return;
    setPacketBusy(true);
    try {
      const slug = (project.design?.name || project.id)
        .toLowerCase()
        .replace(/[^a-z0-9-_]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60);
      await downloadConstructionPacket(
        project.id,
        user.id,
        `construction-record-${slug}.pdf`,
      );
    } catch (_) {
      toast.error("Could not download the construction record.");
    } finally {
      setPacketBusy(false);
    }
  }

  return (
    <section
      className="animate-fade-in-up"
      data-testid="architect-workspace"
      style={{ paddingTop: 12 }}
    >
      <Toaster position="top-right" richColors closeButton />

      <BackBar
        parent={{ to: "/architect/projects", label: "Projects" }}
        trail={[
          { label: "Projects", to: "/architect/projects" },
          { label: project.design?.name || project.parcel?.address || "Project" },
          { label: "Workspace" },
        ]}
      />

      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <header className="mt-4">
        <div className="flex items-center" style={{ gap: 12 }}>
          <span className="wn-label" style={{ color: "#8F3C23" }}>
            Architect Workspace
          </span>
          <StatusPill value={stage} testId="ws-stage-pill" />
          {project.accela_ref?.case_number && (
            <span
              className="tabular-nums"
              style={{
                fontSize: 11.5,
                fontWeight: 700,
                color: "#3A5563",
                letterSpacing: "0.04em",
              }}
              data-testid="ws-accela-ref"
            >
              {project.accela_ref.case_number}
            </span>
          )}
        </div>
        <h1
          className="mt-3"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontSize: "clamp(28px, 4vw, 36px)",
            fontWeight: 600,
            lineHeight: 1.1,
            color: "#1B1F4B",
          }}
          data-testid="ws-title"
        >
          {project.parcel?.address || project.design?.name || "Project workspace"}
        </h1>
        <p
          className="mt-2"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 16,
          }}
        >
          {project.design?.name}
          {project.owner?.name ? ` · ${project.owner.name}` : ""}
          {project.owner?.organization ? ` · ${project.owner.organization}` : ""}
        </p>

        {/* Stage-aware primary action row */}
        <div className="mt-5 flex items-center flex-wrap" style={{ gap: 10 }}>
          <button
            type="button"
            className="wn-btn wn-btn-primary"
            onClick={() => setNoteOpen(true)}
            data-testid="ws-add-note-btn"
          >
            <NotebookPen size={14} />
            Add note
          </button>
          {isCi && !isSigned && (
            <button
              type="button"
              className="wn-btn wn-btn-tertiary"
              onClick={() => setLogFor("foundation")}
              data-testid="ws-log-visit-shortcut"
            >
              <Camera size={14} />
              Log inspection visit
            </button>
          )}
          {packetReady && (
            <button
              type="button"
              onClick={handlePacketDownload}
              disabled={packetBusy}
              className="wn-btn wn-btn-tertiary"
              data-testid="ws-packet-download"
            >
              <Download size={14} />
              {packetBusy ? "Preparing…" : "Construction record (PDF)"}
            </button>
          )}
        </div>
      </header>

      <div style={{ borderTop: "1px solid #D9D8E4", marginTop: 26 }} />

      {/* Stage timeline (visual context — same widget as detail page) */}
      <div data-testid="ws-workflow" style={{ marginTop: 18 }}>
        <WorkflowStrip
          domain="project"
          current={stage}
          dateByStage={(project.stage_history || []).reduce((acc, s) => {
            if (s.stage && s.at) acc[s.stage] = s.at;
            return acc;
          }, {})}
          testId="ws-workflow-strip"
        />
      </div>

      {/* What's next banner */}
      <WsNextActionBanner project={project} navigate={navigate} />

      {/* 2-column body: LEFT = work surface, RIGHT = sidebar reference */}
      <div
        className="mt-2"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.6fr) minmax(0, 1fr)",
          gap: 28,
          alignItems: "start",
        }}
      >
        <div className="min-w-0">
      {/* ── Section: Milestones (only Construction Inspection / Complete) ─ */}
      {(isCi || isComplete) && (
        <Section
          label="Site inspections"
          blurb={
            isSigned
              ? "Final inspection record submitted to DBNS. Inspections are archived."
              : "Verify each inspection in order. Sign-submit unlocks once all three clear."
          }
          testId="ws-section-milestones"
        >
          {!isSigned && <InspectionHelp />}
          {isSigned && (
            <div
              className="wn-card mb-4 flex items-center"
              style={{
                padding: "12px 16px",
                background: "rgba(79,97,73,0.10)",
                borderLeft: "3px solid #4F6149",
                gap: 12,
              }}
              data-testid="ws-signed-banner"
            >
              <ShieldCheck size={16} style={{ color: "#4F6149" }} />
              <div>
                <div style={{ fontWeight: 700, color: "#272636", fontSize: 13 }}>
                  Signed &amp; submitted to DBNS.
                </div>
                <div style={{ color: "#6C6B85", fontSize: 12 }}>
                  Workflow step — not a legal e-signature.
                </div>
              </div>
            </div>
          )}
          <div className="space-y-3">
            {ORDERED.map((k) => (
              <MilestoneRow
                key={k}
                milestone={
                  (project.milestones || {})[k] || {
                    key: k,
                    name: MILESTONE_LABEL[k],
                    status: "Not Started",
                  }
                }
                visits={(project.inspections || []).filter(
                  (i) => i.milestone_key === k,
                )}
                disabled={isSigned}
                onLogVisit={() => setLogFor(k)}
                onVerify={() => setVerifyFor(k)}
                projectId={project.id}
                userId={user.id}
              />
            ))}
          </div>

          {!isSigned && (
            <div
              className="mt-5 wn-card flex items-center justify-between"
              style={{
                padding: "14px 18px",
                gap: 16,
                borderLeft: `3px solid ${allVerified ? "#B14E2F" : "#D9D8E4"}`,
                background: allVerified ? "rgba(177,78,47,0.05)" : "#FBFAFD",
              }}
              data-testid="ws-sign-section"
            >
              <div className="min-w-0">
                <h3
                  style={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 600,
                    fontSize: 17,
                    color: "#1B1F4B",
                    margin: 0,
                  }}
                >
                  Sign &amp; Submit to DBNS
                </h3>
                <p
                  style={{
                    marginTop: 4,
                    color: "#6C6B85",
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  {allVerified
                    ? "All three inspections are verified. Submit your final inspection record to DBNS."
                    : "Verify all three inspections to unlock the final submission."}
                </p>
              </div>
              {allVerified && (
                <button
                  type="button"
                  className="wn-btn wn-btn-accent"
                  onClick={() => setShowSign(true)}
                  data-testid="ws-sign-submit-btn"
                  style={{ whiteSpace: "nowrap" }}
                >
                  <ShieldCheck size={14} />
                  Sign &amp; Submit
                </button>
              )}
            </div>
          )}
        </Section>
      )}

      {/* ── Section: Project journal (notes — always present) ───────────── */}
      <Section
        label="Project journal"
        blurb={
          isComplete
            ? "Archived notes, inspections, and owner messages from the project."
            : "Site visits, owner messages, and reference photos in one timeline. Notes and photos you log are visible to the owner."
        }
        testId="ws-section-journal"
      >
        <Journal
          notes={project.notes || []}
          inspections={project.inspections || []}
          messages={project.messages || []}
          onAdd={() => setNoteOpen(true)}
        />
      </Section>
        </div>

        {/* RIGHT — sidebar: chat, documents, site context */}
        <aside className="min-w-0 space-y-6" data-testid="ws-sidebar">
          <WsQuickChat
            project={project}
            user={user}
            onSent={(updated) =>
              setProject({ ...updated, owner: project.owner ?? updated.owner })
            }
          />
          <WsDocumentsCard
            documents={documents}
            projectId={project.id}
            userId={user.id}
          />
          <WsSiteContextCard project={project} />
        </aside>
      </div>

      {/* Dialogs */}
      <LogVisitDrawer
        open={!!logFor}
        milestoneKey={logFor}
        onClose={() => setLogFor(null)}
        onConfirm={handleLogVisit}
      />
      <VerifyDialog
        open={!!verifyFor}
        milestone={verifyFor ? (project.milestones || {})[verifyFor] : null}
        visits={
          verifyFor
            ? (project.inspections || []).filter(
                (i) => i.milestone_key === verifyFor,
              )
            : []
        }
        onClose={() => setVerifyFor(null)}
        onConfirm={handleVerify}
      />
      <SignDialog
        open={showSign}
        onClose={() => setShowSign(false)}
        onConfirm={handleSignSubmit}
      />
      <NoteDrawer
        open={noteOpen}
        onClose={() => setNoteOpen(false)}
        onConfirm={handleAddNote}
        stage={stage}
      />
    </section>
  );
}

// ─── New: stage banner + sidebar cards ────────────────────────────────────

const WS_TONES = {
  action: { bg: "rgba(177,78,47,0.06)", border: "#B14E2F" },
  waiting: { bg: "#FBFAFD", border: "#5B6196" },
  done: { bg: "rgba(79,97,73,0.06)", border: "#4F6149" },
};

function WsNextActionBanner({ project, navigate }) {
  const action = projectNextAction(project);
  if (!action) return null;
  const tone = WS_TONES[action.tone] || WS_TONES.waiting;
  return (
    <div
      className="mt-5"
      data-testid="ws-next-action"
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        alignItems: "center",
        gap: 16,
        padding: "12px 18px",
        background: tone.bg,
        border: "1px solid #ECEBF1",
        borderLeft: `3px solid ${tone.border}`,
        borderRadius: 4,
      }}
    >
      <div className="min-w-0">
        <div
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 600,
            fontSize: 15,
            color: "#1B1F4B",
          }}
        >
          {action.headline}
        </div>
        <p
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            color: "#6C6B85",
            fontSize: 13.5,
            marginTop: 2,
            lineHeight: 1.45,
          }}
        >
          {action.blurb}
        </p>
      </div>
      {action.cta && action.cta.to !== window.location.pathname && (
        <button
          type="button"
          className={
            action.tone === "action"
              ? "wn-btn wn-btn-primary"
              : "wn-btn wn-btn-tertiary"
          }
          onClick={() => navigate(action.cta.to)}
          data-testid="ws-next-action-cta"
          style={{ whiteSpace: "nowrap" }}
        >
          {action.cta.label}
        </button>
      )}
    </div>
  );
}

function WsQuickChat({ project, user, onSent }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const all = project.messages || [];
  const messages = all.slice(-3);
  const ownerName =
    project.owner?.name || project.owner?.organization || "the owner";
  async function send() {
    if (!text.trim() || !user?.id) return;
    setBusy(true);
    try {
      const updated = await sendProjectMessage(project.id, user.id, text.trim());
      onSent?.(updated);
      setText("");
    } catch (_) {
      /* swallow */
    } finally {
      setBusy(false);
    }
  }
  return (
    <section
      className="wn-card"
      style={{ padding: "16px 18px" }}
      data-testid="ws-quick-chat"
    >
      <header className="flex items-center justify-between" style={{ gap: 10 }}>
        <div className="flex items-center" style={{ gap: 8 }}>
          <MessageSquare size={14} style={{ color: "#8F3C23" }} />
          <h3
            className="wn-label"
            style={{ color: "#8F3C23", letterSpacing: "0.06em" }}
          >
            Quick chat
          </h3>
        </div>
        {all.length > messages.length && (
          <span
            className="tabular-nums"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#9AA0C0",
              fontSize: 11.5,
            }}
          >
            {all.length} total
          </span>
        )}
      </header>
      <p
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "#6C6B85",
          fontSize: 12,
          marginTop: 2,
          lineHeight: 1.45,
        }}
      >
        Send {ownerName} a quick update without leaving the workspace.
      </p>
      {messages.length > 0 && (
        <ul
          className="mt-3"
          style={{ listStyle: "none", padding: 0, margin: 0 }}
          data-testid="ws-quick-chat-list"
        >
          {messages.map((m, i) => (
            <li
              key={m.id || i}
              style={{
                padding: "8px 0",
                borderTop: i === 0 ? "none" : "1px solid #ECEBF1",
              }}
            >
              <div className="flex items-baseline" style={{ gap: 6 }}>
                <span
                  style={{
                    fontFamily: '"Nunito Sans", sans-serif',
                    fontWeight: 700,
                    fontSize: 11.5,
                    color: m.from_role === "architect" ? "#8F3C23" : "#1B1F4B",
                  }}
                >
                  {m.from_name || (m.from_role === "architect" ? "You" : "Owner")}
                </span>
                <span
                  className="tabular-nums"
                  style={{
                    color: "#9AA0C0",
                    fontSize: 11,
                    fontFamily: '"Newsreader", Georgia, serif',
                    fontStyle: "italic",
                  }}
                >
                  {m.at
                    ? new Date(m.at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })
                    : ""}
                </span>
              </div>
              <p
                style={{
                  color: "#272636",
                  fontSize: 12.5,
                  marginTop: 2,
                  lineHeight: 1.4,
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {m.body || m.text || ""}
              </p>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3 flex items-end" style={{ gap: 6 }}>
        <textarea
          className="wn-input"
          rows={2}
          style={{ flex: 1, padding: 8, fontSize: 12.5, lineHeight: 1.4 }}
          placeholder={`Message ${ownerName}…`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              send();
            }
          }}
          data-testid="ws-quick-chat-composer"
        />
        <button
          type="button"
          className="wn-btn wn-btn-primary"
          onClick={send}
          disabled={busy || !text.trim()}
          data-testid="ws-quick-chat-send"
          style={{ paddingLeft: 10, paddingRight: 10, fontSize: 12 }}
        >
          <Send size={11} />
          {busy ? "…" : "Send"}
        </button>
      </div>
    </section>
  );
}

const DOC_LABELS = {
  feasibility_report: "Feasibility Report",
  lender_underwriting: "Lender Underwriting",
  ihcda_guidance: "IHCDA Guidance",
  rezoning_addendum: "Rezoning Addendum",
  final_record: "Final Record",
};

function WsDocumentsCard({ documents, projectId, userId }) {
  const [busyId, setBusyId] = useState(null);
  async function pull(d) {
    setBusyId(d.id);
    try {
      await downloadProjectDocument(
        projectId,
        d.id,
        userId,
        `${d.label || d.doc_type}.pdf`,
      );
    } catch (_) {
      /* swallow */
    } finally {
      setBusyId(null);
    }
  }
  return (
    <section
      className="wn-card"
      style={{ padding: "16px 18px" }}
      data-testid="ws-documents"
    >
      <header className="flex items-center" style={{ gap: 8 }}>
        <FileText size={14} style={{ color: "#8F3C23" }} />
        <h3
          className="wn-label"
          style={{ color: "#8F3C23", letterSpacing: "0.06em" }}
        >
          Documents
        </h3>
      </header>
      {documents === null ? (
        <div
          className="shimmer mt-2"
          style={{ height: 36, borderRadius: 6 }}
        />
      ) : documents.length === 0 ? (
        <p
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#9AA0C0",
            fontSize: 12.5,
            marginTop: 6,
            lineHeight: 1.45,
          }}
          data-testid="ws-documents-empty"
        >
          No documents yet. They appear here as the owner generates them.
        </p>
      ) : (
        <ul
          className="mt-2"
          style={{ listStyle: "none", padding: 0, margin: 0 }}
        >
          {documents.map((d, i) => (
            <li
              key={d.id}
              className="flex items-center justify-between"
              style={{
                gap: 8,
                padding: "8px 0",
                borderTop: i === 0 ? "none" : "1px solid #ECEBF1",
              }}
              data-testid={`ws-document-${d.id}`}
            >
              <div className="min-w-0" style={{ flex: 1 }}>
                <div
                  style={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#1B1F4B",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {d.label || DOC_LABELS[d.doc_type] || d.doc_type}
                </div>
                <div
                  className="tabular-nums"
                  style={{
                    fontFamily: '"Newsreader", Georgia, serif',
                    fontStyle: "italic",
                    color: "#6C6B85",
                    fontSize: 11.5,
                    marginTop: 1,
                  }}
                >
                  {d.generated_at
                    ? new Date(d.generated_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "—"}
                </div>
              </div>
              <button
                type="button"
                onClick={() => pull(d)}
                disabled={busyId === d.id}
                className="wn-btn wn-btn-tertiary"
                style={{ paddingLeft: 8, paddingRight: 8, fontSize: 11.5 }}
                data-testid={`ws-document-pdf-${d.id}`}
              >
                <Download size={11} />
                {busyId === d.id ? "…" : "PDF"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function WsSiteContextCard({ project }) {
  const parcel = project.parcel || {};
  const design = project.design || {};
  return (
    <section
      className="wn-card"
      style={{ padding: "16px 18px" }}
      data-testid="ws-site-context"
    >
      <header className="flex items-center" style={{ gap: 8 }}>
        <h3
          className="wn-label"
          style={{ color: "#6C6B85", letterSpacing: "0.06em" }}
        >
          Site context
        </h3>
      </header>
      <div className="mt-2 space-y-3">
        <div>
          <div
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 14,
              color: "#1B1F4B",
            }}
          >
            {parcel.address || "Address pending"}
          </div>
          <div
            className="tabular-nums"
            style={{
              fontFamily: '"Nunito Sans", sans-serif',
              color: "#3A5563",
              fontSize: 12,
              marginTop: 2,
            }}
          >
            {parcel.zoning ? `${parcel.zoning}` : "Zoning —"}
            {parcel.width_ft && parcel.depth_ft
              ? ` · ${parcel.width_ft}′ × ${parcel.depth_ft}′`
              : ""}
            {parcel.neighborhood ? ` · ${parcel.neighborhood}` : ""}
          </div>
        </div>
        <div style={{ borderTop: "1px solid #ECEBF1", paddingTop: 10 }}>
          <div
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 14,
              color: "#1B1F4B",
            }}
          >
            {design.name || "Design"}
          </div>
          <div
            className="tabular-nums"
            style={{
              fontFamily: '"Nunito Sans", sans-serif',
              color: "#3A5563",
              fontSize: 12,
              marginTop: 2,
            }}
          >
            {design.sqft ? `${design.sqft.toLocaleString()} sqft` : "—"}
            {design.beds
              ? ` · ${design.beds}bd / ${design.baths || 0}ba`
              : ""}
          </div>
        </div>
        <div style={{ borderTop: "1px solid #ECEBF1", paddingTop: 10 }}>
          <div
            className="wn-label"
            style={{ color: "#6C6B85", fontSize: 10, letterSpacing: "0.06em" }}
          >
            Owner / builder
          </div>
          <div
            style={{
              color: "#272636",
              fontSize: 13,
              marginTop: 3,
              fontFamily: '"Nunito Sans", sans-serif',
            }}
          >
            {project.owner?.organization || project.owner?.name || "—"}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────
function InspectionHelp() {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="mb-4"
      style={{
        borderLeft: "3px solid #5B6196",
        background: "#FBFAFD",
        padding: "10px 14px",
        borderRadius: 4,
      }}
      data-testid="ws-inspection-help"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full"
        style={{
          gap: 12,
          background: "transparent",
          border: "none",
          padding: 0,
          cursor: "pointer",
          color: "#1B1F4B",
          textAlign: "left",
        }}
        aria-expanded={open}
        data-testid="ws-inspection-help-toggle"
      >
        <span
          className="wn-label"
          style={{ color: "#5B6196", letterSpacing: "0.06em" }}
        >
          How this works
        </span>
        <span
          style={{
            fontFamily: '"Nunito Sans", sans-serif',
            fontSize: 11,
            color: "#5B6196",
            fontWeight: 700,
          }}
        >
          {open ? "Hide ↑" : "Show ↓"}
        </span>
      </button>
      {open && (
        <ol
          className="mt-2"
          style={{
            paddingLeft: 18,
            color: "#272636",
            fontSize: 12.5,
            lineHeight: 1.55,
            fontFamily: '"Newsreader", Georgia, serif',
          }}
        >
          <li>
            <strong>Log a site visit</strong> on each inspection — visit date,
            field notes, optional photos, optional variances. You can log
            multiple visits per inspection.
          </li>
          <li>
            <strong>Verify</strong> once you’re satisfied. The inspection
            status flips to Verified with a date stamp.
          </li>
          <li>
            When all three (<em>Foundation, Rough-in, Final / Occupancy</em>)
            are Verified, the{" "}
            <strong>Sign &amp; Submit to DBNS</strong> tile lights up. Click
            to forward the final inspection record and close the project.
          </li>
        </ol>
      )}
    </div>
  );
}

function Section({ label, blurb, children, testId, muted }) {
  return (
    <div className="mt-8" data-testid={testId}>
      <h2
        className="wn-label"
        style={{ color: muted ? "#6C6B85" : "#8F3C23" }}
      >
        {label}
      </h2>
      {blurb && (
        <p
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 13.5,
            marginTop: 4,
            maxWidth: 640,
          }}
        >
          {blurb}
        </p>
      )}
      <div className="mt-4">{children}</div>
    </div>
  );
}

function Fact({ label, children }) {
  return (
    <div>
      <div
        className="wn-label"
        style={{ color: "#6C6B85", fontSize: 10.5, letterSpacing: "0.06em" }}
      >
        {label}
      </div>
      <div
        style={{
          color: "#272636",
          fontSize: 14,
          fontWeight: 600,
          marginTop: 2,
          fontFamily: '"Nunito Sans", sans-serif',
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ─── Milestone row (simplified, horizontal-first) ─────────────────────────
function MilestoneRow({
  milestone,
  visits,
  disabled,
  onLogVisit,
  onVerify,
  projectId,
  userId,
}) {
  const done = milestone.status === "Verified";
  const inProg = milestone.status === "Visit Logged";
  const accent = done ? "#4F6149" : inProg ? "#8a651f" : "#9AA0C0";
  return (
    <div
      className="wn-card"
      style={{
        padding: "14px 18px",
        borderLeft: `3px solid ${accent}`,
      }}
      data-testid={`milestone-${milestone.key}`}
    >
      <div className="flex items-start justify-between" style={{ gap: 16 }}>
        <div className="flex items-start" style={{ gap: 12, flex: 1 }}>
          {done ? (
            <CheckCircle2
              size={20}
              style={{ color: accent, flexShrink: 0, marginTop: 2 }}
              strokeWidth={2.2}
            />
          ) : (
            <Circle
              size={20}
              style={{ color: accent, flexShrink: 0, marginTop: 2 }}
            />
          )}
          <div className="min-w-0">
            <h3
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: 16,
                color: "#1B1F4B",
                margin: 0,
              }}
            >
              {milestone.name}
            </h3>
            <div
              className="tabular-nums"
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "#6C6B85",
                fontSize: 12.5,
                marginTop: 2,
              }}
              data-testid={`milestone-status-${milestone.key}`}
            >
              {done && milestone.verified_at
                ? `Verified ${fmtDate(milestone.verified_at)}`
                : inProg && milestone.last_visit_at
                  ? `Last visit ${fmtDate(milestone.last_visit_at)}`
                  : "Not started"}
              {visits.length > 0 && ` · ${visits.length} visit${visits.length === 1 ? "" : "s"}`}
            </div>
          </div>
        </div>
        {!disabled && !done && (
          <div className="flex items-center" style={{ gap: 8 }}>
            <button
              type="button"
              className="wn-btn wn-btn-tertiary"
              onClick={onLogVisit}
              data-testid={`log-visit-${milestone.key}`}
              style={{ paddingLeft: 12, paddingRight: 12, fontSize: 12.5 }}
            >
              <Camera size={12} />
              Log visit
            </button>
            {inProg && (
              <button
                type="button"
                className="wn-btn wn-btn-primary"
                onClick={onVerify}
                data-testid={`verify-${milestone.key}`}
                style={{ paddingLeft: 12, paddingRight: 12, fontSize: 12.5 }}
              >
                <CheckCircle2 size={12} />
                Verify
              </button>
            )}
          </div>
        )}
      </div>
      {visits.length > 0 && (
        <ul
          className="mt-3"
          style={{ listStyle: "none", padding: 0, margin: 0 }}
          data-testid={`milestone-visits-${milestone.key}`}
        >
          {visits.slice(-2).map((v) => (
            <li
              key={v.id}
              style={{
                padding: "8px 0 0",
                borderTop: "1px solid #ECEBF1",
                marginTop: 8,
              }}
            >
              <div className="flex items-baseline" style={{ gap: 8 }}>
                <span
                  className="tabular-nums"
                  style={{
                    fontFamily: '"Nunito Sans", sans-serif',
                    color: "#3A5563",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {fmtDate(v.visit_date)}
                </span>
                {v.field_photos?.length > 0 && (
                  <span style={{ color: "#9AA0C0", fontSize: 11.5 }}>
                    {v.field_photos.length} photo
                    {v.field_photos.length === 1 ? "" : "s"}
                  </span>
                )}
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await downloadInspectionPdf(
                        projectId,
                        v.id,
                        userId,
                        `inspection-${v.milestone_key}-${v.id}.pdf`,
                      );
                    } catch (_) {
                      /* ignore */
                    }
                  }}
                  data-testid={`milestone-visit-pdf-${v.id}`}
                  style={{
                    fontFamily: '"Nunito Sans", sans-serif',
                    fontWeight: 700,
                    fontSize: 11.5,
                    color: "#8F3C23",
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    letterSpacing: "0.02em",
                  }}
                >
                  ↓ Field report
                </button>
              </div>
              <div
                style={{
                  color: "#272636",
                  fontSize: 13,
                  marginTop: 3,
                  lineHeight: 1.5,
                }}
              >
                {v.field_notes}
              </div>
              {v.variances && (
                <div
                  style={{
                    marginTop: 3,
                    fontSize: 11.5,
                    color: "#8a651f",
                    fontWeight: 600,
                  }}
                >
                  Variance: {v.variances}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Unified journal (notes + inspections + messages) ────────────────────
function Journal({ notes, inspections, messages, onAdd }) {
  // Merge notes, inspections, and project messages into a single timeline,
  // newest first.
  const items = useMemo(() => {
    const all = [];
    notes.forEach((n) =>
      all.push({
        type: "note",
        at: n.logged_at,
        ref: n,
      }),
    );
    inspections.forEach((v) =>
      all.push({
        type: "visit",
        at: v.logged_at || v.visit_date,
        ref: v,
      }),
    );
    (messages || []).forEach((m) =>
      all.push({
        type: "message",
        at: m.at,
        ref: m,
      }),
    );
    return all.sort(
      (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
    );
  }, [notes, inspections, messages]);

  if (items.length === 0) {
    return (
      <div
        className="wn-card"
        style={{ padding: "20px 22px", textAlign: "left" }}
        data-testid="ws-journal-empty"
      >
        <p
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#9AA0C0",
            fontSize: 14,
            lineHeight: 1.55,
            maxWidth: 540,
          }}
        >
          No entries yet. Add a note to capture site visits, owner
          conversations, or reference photos — they’ll be visible to the
          owner and stored on the project record.
        </p>
        <button
          type="button"
          className="wn-btn wn-btn-tertiary mt-3 inline-flex"
          onClick={onAdd}
          data-testid="ws-journal-add"
        >
          <NotebookPen size={14} />
          Add first note
        </button>
      </div>
    );
  }
  return (
    <ul
      className="space-y-3"
      style={{ listStyle: "none", padding: 0, margin: 0 }}
      data-testid="ws-journal"
    >
      {items.map((it, i) => {
        const accent =
          it.type === "visit"
            ? "#B14E2F"
            : it.type === "message"
              ? it.ref.from_role === "architect"
                ? "#8F3C23"
                : "#3A5563"
              : "#5B6196";
        const labelColor =
          it.type === "visit"
            ? "#8F3C23"
            : it.type === "message"
              ? it.ref.from_role === "architect"
                ? "#8F3C23"
                : "#3A5563"
              : "#5B6196";
        let label;
        if (it.type === "visit") {
          label = `Inspection · ${MILESTONE_LABEL[it.ref.milestone_key] || it.ref.label}`;
        } else if (it.type === "message") {
          label =
            it.ref.from_role === "architect"
              ? `Message · You`
              : `Message · ${it.ref.from_name || "Owner"}`;
        } else {
          label = `Note${
            it.ref.stage_at_entry ? ` · ${it.ref.stage_at_entry}` : ""
          }`;
        }
        return (
          <li
            key={(it.ref.id || i) + it.type}
            className="wn-card"
            style={{
              padding: "14px 18px",
              borderLeft: `3px solid ${accent}`,
            }}
            data-testid={`ws-journal-${it.type}-${it.ref.id || i}`}
          >
            <div className="flex items-baseline" style={{ gap: 8 }}>
              <span
                className="wn-label"
                style={{
                  color: labelColor,
                  letterSpacing: "0.06em",
                  fontSize: 10.5,
                }}
              >
                {label}
              </span>
              <span
                className="tabular-nums"
                style={{
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontStyle: "italic",
                  color: "#9AA0C0",
                  fontSize: 12,
                }}
              >
                {fmtDate(it.at)}
              </span>
            </div>
            <div
              style={{
                color: "#272636",
                fontSize: 14,
                marginTop: 6,
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
              }}
            >
              {it.type === "visit"
                ? it.ref.field_notes
                : it.ref.body || it.ref.text || ""}
            </div>
            {it.type === "visit" && it.ref.variances && (
              <div
                style={{
                  marginTop: 4,
                  fontSize: 12,
                  color: "#8a651f",
                  fontWeight: 600,
                }}
              >
                Variance: {it.ref.variances}
              </div>
            )}
            {(it.ref.field_photos?.length > 0 ||
              it.ref.photos?.length > 0) && (
              <PhotoStrip
                photos={it.ref.field_photos || it.ref.photos}
                testId={`ws-journal-photos-${it.ref.id}`}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

function PhotoStrip({ photos, testId }) {
  return (
    <div
      className="mt-3 flex items-center flex-wrap"
      style={{ gap: 8 }}
      data-testid={testId}
    >
      {photos.map((p) => {
        const src = p.url ? assetUrl(p.url) : null;
        if (!src) return null;
        return (
          <a
            key={p.id || p.url}
            href={src}
            target="_blank"
            rel="noreferrer"
            style={{
              width: 64,
              height: 48,
              borderRadius: 4,
              border: "1px solid #D9D8E4",
              backgroundColor: "#ECEBF1",
              backgroundImage: `url(${src})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              display: "block",
            }}
            aria-label={p.label || p.original_name || "photo"}
          />
        );
      })}
    </div>
  );
}

// ─── Add-note drawer (stage-agnostic) ─────────────────────────────────────
function NoteDrawer({ open, onClose, onConfirm, stage }) {
  const [body, setBody] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setBody("");
      setPhotos([]);
      setBusy(false);
    }
  }, [open]);

  async function go() {
    if (!body.trim()) return;
    setBusy(true);
    await onConfirm({ body: body.trim(), photos });
    setBusy(false);
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="border"
        style={{
          backgroundColor: "#FBFAFD",
          borderColor: "#D9D8E4",
          borderRadius: 12,
          fontFamily: '"Nunito Sans", sans-serif',
          maxWidth: 600,
        }}
        data-testid="ws-note-drawer"
      >
        <DialogHeader>
          <DialogTitle
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 22,
              color: "#1B1F4B",
            }}
          >
            Add a note
          </DialogTitle>
          <DialogDescription
            style={{ color: "#6C6B85", fontSize: 13, lineHeight: 1.5 }}
          >
            Capture a site visit, owner conversation, or anything else worth
            preserving on the project record. Notes are visible to the owner.
            {stage && (
              <>
                {" "}
                Tagged at stage <strong>{stage}</strong>.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 space-y-4">
          <textarea
            className="wn-input"
            style={{ minHeight: 120, padding: 10 }}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="What happened?"
            data-testid="ws-note-body"
          />
          <FieldPhotos value={photos} onChange={setPhotos} />
        </div>
        <DialogFooter className="mt-3">
          <button
            className="wn-btn wn-btn-tertiary"
            onClick={onClose}
            disabled={busy}
            data-testid="ws-note-cancel"
          >
            Cancel
          </button>
          <button
            className="wn-btn wn-btn-primary"
            onClick={go}
            disabled={busy || !body.trim()}
            data-testid="ws-note-save"
          >
            {busy ? "Saving…" : "Save note"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Existing dialogs (log visit, verify, sign-submit) — preserved ────────
function LogVisitDrawer({ open, milestoneKey, onClose, onConfirm }) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [variances, setVariances] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setDate(new Date().toISOString().slice(0, 10));
      setNotes("");
      setVariances("");
      setPhotos([]);
    }
  }, [open]);

  async function go() {
    if (!notes.trim()) return;
    setBusy(true);
    await onConfirm({
      visit_date: date,
      field_notes: notes.trim(),
      variances: variances.trim(),
      field_photos: photos,
    });
    setBusy(false);
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="border"
        style={{
          backgroundColor: "#FBFAFD",
          borderColor: "#D9D8E4",
          borderRadius: 12,
          fontFamily: '"Nunito Sans", sans-serif',
          maxWidth: 640,
        }}
        data-testid="log-visit-drawer"
      >
        <DialogHeader>
          <DialogTitle
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 22,
              color: "#1B1F4B",
            }}
          >
            Log inspection visit
            {milestoneKey && ` · ${MILESTONE_LABEL[milestoneKey] || milestoneKey}`}
          </DialogTitle>
          <DialogDescription
            style={{ color: "#6C6B85", fontSize: 13, lineHeight: 1.5 }}
          >
            Record what you saw on site. Visit notes are visible to the owner.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 space-y-4">
          <div>
            <label className="wn-label">Visit date</label>
            <input
              type="date"
              className="wn-input tabular-nums mt-1"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="visit-date"
            />
          </div>
          <div>
            <label className="wn-label">Field notes</label>
            <textarea
              className="wn-input mt-1"
              style={{ minHeight: 90, padding: 10 }}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Walked the site; observed…"
              data-testid="visit-notes"
            />
          </div>
          <div>
            <label className="wn-label">Variances or issues</label>
            <textarea
              className="wn-input mt-1"
              style={{ minHeight: 60, padding: 10 }}
              value={variances}
              onChange={(e) => setVariances(e.target.value)}
              placeholder="Optional — anything off-spec."
              data-testid="visit-variances"
            />
          </div>
          <FieldPhotos value={photos} onChange={setPhotos} />
        </div>
        <DialogFooter className="mt-3">
          <button
            className="wn-btn wn-btn-tertiary"
            onClick={onClose}
            disabled={busy}
            data-testid="visit-cancel"
          >
            Cancel
          </button>
          <button
            className="wn-btn wn-btn-primary"
            onClick={go}
            disabled={busy || !notes.trim()}
            data-testid="visit-save"
          >
            {busy ? "Saving…" : "Save visit"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldPhotos({ value, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const max = 6;

  async function onPick(file) {
    if (!file) return;
    if (value.length >= max) {
      setErr(`Up to ${max} photos.`);
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const meta = await uploadFile(file, "field_photo");
      onChange([
        ...value,
        {
          id: meta.id,
          label: "Photo",
          original_name: meta.original_name,
          mime: meta.mime,
          size: meta.size,
          url: meta.url,
        },
      ]);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }
  return (
    <div data-testid="ws-photos">
      <div className="wn-label">Photos ({value.length}/{max})</div>
      <div className="mt-2 space-y-2">
        {value.map((f, i) => (
          <UploadedRow
            key={f.id}
            file={f}
            onRemove={() => onChange(value.filter((_, idx) => idx !== i))}
            testidPrefix={`ws-photo-${i}`}
          />
        ))}
      </div>
      {value.length < max && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="mt-2"
          style={{
            width: "100%",
            backgroundColor: "#FBFAFD",
            border: "1px dashed #9AA0C0",
            borderRadius: 8,
            padding: 12,
            display: "flex",
            alignItems: "center",
            gap: 10,
            cursor: busy ? "wait" : "pointer",
            color: "#1B1F4B",
            textAlign: "left",
          }}
          data-testid="ws-photo-trigger"
        >
          <Upload size={14} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>
              {busy ? "Uploading…" : "Add a photo"}
            </div>
            <div style={{ fontSize: 11, color: "#6C6B85", marginTop: 2 }}>
              JPG, PNG, or PDF · up to 10 MB.
            </div>
          </div>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/jpeg,image/png"
        hidden
        onChange={(e) => onPick(e.target.files?.[0])}
      />
      {err && (
        <div style={{ marginTop: 6, color: "#A23B2E", fontSize: 12 }}>{err}</div>
      )}
    </div>
  );
}

function VerifyDialog({ open, milestone, visits, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  if (!milestone) return null;
  const lastVisit = visits[visits.length - 1];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="border"
        style={{
          backgroundColor: "#FBFAFD",
          borderColor: "#D9D8E4",
          borderRadius: 12,
          fontFamily: '"Nunito Sans", sans-serif',
          maxWidth: 520,
        }}
        data-testid="verify-dialog"
      >
        <DialogHeader>
          <DialogTitle
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 22,
              color: "#1B1F4B",
            }}
          >
            Verify {milestone.name}?
          </DialogTitle>
          <DialogDescription style={{ color: "#6C6B85", fontSize: 13 }}>
            Records your professional sign-off on this milestone. The owner is
            notified.
          </DialogDescription>
        </DialogHeader>
        {lastVisit && (
          <div
            className="wn-card mt-2"
            style={{ padding: "12px 14px", backgroundColor: "#FBFAFD" }}
          >
            <div className="wn-label">Last visit</div>
            <div
              className="tabular-nums"
              style={{
                fontSize: 12,
                color: "#3A5563",
                fontWeight: 700,
                marginTop: 2,
              }}
            >
              {fmtDate(lastVisit.visit_date)} ·{" "}
              {(lastVisit.field_photos || []).length} photo
              {(lastVisit.field_photos || []).length === 1 ? "" : "s"}
            </div>
            <div
              style={{
                fontSize: 13,
                color: "#272636",
                marginTop: 4,
                lineHeight: 1.5,
              }}
            >
              {lastVisit.field_notes}
            </div>
          </div>
        )}
        <DialogFooter className="mt-3">
          <button
            className="wn-btn wn-btn-tertiary"
            onClick={onClose}
            disabled={busy}
            data-testid="verify-cancel"
          >
            Cancel
          </button>
          <button
            className="wn-btn wn-btn-accent"
            onClick={async () => {
              setBusy(true);
              await onConfirm();
              setBusy(false);
            }}
            disabled={busy}
            data-testid="verify-confirm"
          >
            {busy ? "Verifying…" : "Confirm verification"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SignDialog({ open, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="border"
        style={{
          backgroundColor: "#FBFAFD",
          borderColor: "#D9D8E4",
          borderRadius: 12,
          fontFamily: '"Nunito Sans", sans-serif',
          maxWidth: 560,
        }}
        data-testid="sign-dialog"
      >
        <DialogHeader>
          <DialogTitle
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 22,
              color: "#1B1F4B",
            }}
          >
            Sign &amp; Submit to DBNS?
          </DialogTitle>
          <DialogDescription style={{ color: "#6C6B85", fontSize: 13 }}>
            Records your professional sign-off and forwards the final inspection
            record to DBNS. Project advances to Complete.
          </DialogDescription>
        </DialogHeader>
        <ul
          className="mt-2"
          style={{ fontSize: 13, color: "#272636", paddingLeft: 18 }}
        >
          <li>Final inspection record forwarded to DBNS.</li>
          <li>Project lifecycle advances to Complete.</li>
          <li>Owner notified by in-app + simulated email.</li>
        </ul>
        <p
          className="mt-2"
          style={{
            fontStyle: "italic",
            fontFamily: '"Newsreader", Georgia, serif',
            color: "#6C6B85",
            fontSize: 12,
          }}
        >
          Workflow step — not a legal electronic signature.
        </p>
        <DialogFooter className="mt-3">
          <button
            className="wn-btn wn-btn-tertiary"
            onClick={onClose}
            disabled={busy}
            data-testid="sign-cancel"
          >
            Cancel
          </button>
          <button
            className="wn-btn wn-btn-accent"
            onClick={async () => {
              setBusy(true);
              await onConfirm();
              setBusy(false);
            }}
            disabled={busy}
            data-testid="sign-confirm"
          >
            {busy ? "Submitting…" : "Confirm sign-off"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}
